import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Package, ScanLine } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { inventoryService, warehousesService } from '@/services/inventory';
import { finishedGoodsService } from '@/services/finishedGoods';
import type { FinishedGoodInwardPayload } from '@/types/finished-goods';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BARCODE_PATTERN = /^[A-Za-z0-9._:\-]{1,64}$/;

/**
 * Finished goods inward — receive a batch of N physical units of a single
 * SKU into a single warehouse. Each unit gets its own barcode (paste from
 * the print run, or scan via wedge scanner). Each barcode becomes a row in
 * the finished_goods table AND bumps the parent inventory item's stock
 * level by 1.
 */
export function FinishedGoodInwardSheet({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [itemId, setItemId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [barcodesText, setBarcodesText] = useState('');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');

  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { status: 'ACTIVE', forSelect: true }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });

  const warehousesQuery = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (open) {
      setItemId('');
      setWarehouseId('');
      setBarcodesText('');
      setReference('');
      setReason('');
    }
  }, [open]);

  // Parse pasted text into a clean barcode list (split on whitespace +
  // commas, dedupe, validate against the BE pattern).
  const parsed = barcodesText
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const unique = Array.from(new Set(parsed));
  const valid = unique.filter((b) => BARCODE_PATTERN.test(b));
  const invalid = unique.filter((b) => !BARCODE_PATTERN.test(b));

  const mutation = useMutation({
    mutationFn: (payload: FinishedGoodInwardPayload) => finishedGoodsService.inward(payload),
    onSuccess: (res) => {
      toast.success(
        `Received ${res.inwardCount} ${res.inwardCount === 1 ? 'unit' : 'units'}`,
      );
      qc.invalidateQueries({ queryKey: ['finished-goods'] });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!itemId || !warehouseId || valid.length === 0) return;
    mutation.mutate({
      itemId,
      warehouseId,
      barcodes: valid,
      referenceType: reference ? 'po' : undefined,
      referenceId: reference || undefined,
      reason: reason.trim() || undefined,
    });
  };

  const items = itemsQuery.data?.items ?? [];
  const warehouses = warehousesQuery.data ?? [];

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Receive finished goods batch"
      description="Each barcode becomes one physical unit in stock. Paste a printed barcode list, or scan with a wedge scanner — the form auto-deduplicates."
      submitLabel={`Receive ${valid.length || 0} ${valid.length === 1 ? 'unit' : 'units'}`}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="2xl"
    >
      <div className="rounded-md border border-info/20 bg-info/5 px-3 py-2.5">
        <div className="flex items-start gap-2 text-[12.5px] text-foreground">
          <Package className="mt-0.5 h-4 w-4 shrink-0 text-info" />
          <div>
            <span className="font-medium">One barcode = one physical unit.</span>{' '}
            <span className="text-muted-foreground">
              Stock level bumps by the number of barcodes received. Each unit becomes a row in
              Finished Goods that you can later allocate to an order, dispatch, or return.
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="SKU" htmlFor="item" required>
          <Select value={itemId} onValueChange={setItemId}>
            <SelectTrigger id="item">
              <SelectValue placeholder={itemsQuery.isLoading ? 'Loading SKUs…' : 'Select SKU'} />
            </SelectTrigger>
            <SelectContent>
              {items.map((it) => (
                <SelectItem key={it.id} value={it.id}>
                  <span className="font-mono text-[11px] text-muted-foreground">{it.sku}</span>
                  <span className="ml-2">{it.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField label="Warehouse" htmlFor="warehouse" required>
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger id="warehouse">
              <SelectValue placeholder={warehousesQuery.isLoading ? 'Loading…' : 'Select warehouse'} />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  <span className="font-mono text-[11px] text-muted-foreground">{w.code}</span>
                  <span className="ml-2">{w.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>

      <FormField
        label="Barcodes"
        htmlFor="barcodes"
        required
        hint={
          <span className="inline-flex items-center gap-1.5">
            <ScanLine className="h-3 w-3" />
            One per line, or paste comma-separated. Wedge scanners auto-append Enter — works fine.
          </span>
        }
      >
        <Textarea
          id="barcodes"
          value={barcodesText}
          onChange={(e) => setBarcodesText(e.target.value)}
          placeholder={'zfb_live_a1b2c3d4e5f6\nzfb_live_b2c3d4e5f6a7\nzfb_live_c3d4e5f6a7b8'}
          rows={6}
          className="font-mono text-[12px]"
        />
      </FormField>

      {(unique.length > 0 || invalid.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <Badge variant="muted">{unique.length} unique</Badge>
          {valid.length > 0 && (
            <Badge variant="success">{valid.length} valid</Badge>
          )}
          {invalid.length > 0 && (
            <Badge variant="destructive">{invalid.length} invalid</Badge>
          )}
          {invalid.length > 0 && (
            <span className="text-muted-foreground">
              {invalid.slice(0, 3).map((b) => `"${b}"`).join(', ')}
              {invalid.length > 3 ? '…' : ''}
            </span>
          )}
        </div>
      )}

      <FormField label="Reference (PO #, GRN #)" htmlFor="reference" hint="Optional — links the receipt for audit.">
        <Input
          id="reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="PO-2026-0042"
        />
      </FormField>

      <FormField label="Notes" htmlFor="reason">
        <Textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Manufacturing batch · QC passed · Scanned by Riya"
          rows={2}
        />
      </FormField>
    </FormSheet>
  );
}
